import icArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Pagination } from "@/components/ui/pagination";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { formatDate, STATUS_CONFIG_ADMIN } from "@/lib/utils";
import {
  IAgentStatus,
  UserAgentRes,
  UserManagementService,
} from "@/services/user-management-service";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const PAGE_SIZE = 10;

const ManageUserAgentListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [agents, setAgents] = useState<UserAgentRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userName, setUserName] = useState("");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<UserAgentRes | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [userResponse, agentResponse] = await Promise.all([
          UserManagementService.getById(id),
          UserManagementService.getListUserAgent({
            user_Id: id,
            page: currentPage,
            page_size: PAGE_SIZE,
          }),
        ]);

        const userData = userResponse.data.data;
        const agentsData = agentResponse.data.data?.items ?? [];
        const meta = agentResponse.data.data;

        if (userData) {
          setUserName(userData.requester_name);
        }

        setAgents(agentsData);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      } catch (err) {
        setError("Failed to load agents");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentPage]);

  const handleBack = () => {
    navigate(`/manage-user/${id}`);
  };

  const handleDeleteClick = (agent: UserAgentRes) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete || !id) return;

    try {
      setDeleteLoading(true);
      await UserManagementService.deleteUserAgent(id, agentToDelete.id);
      toast({
        title: "에이전트가 삭제되었습니다.",
      });
      // Refresh the list
      const agentResponse = await UserManagementService.getListUserAgent({
        user_Id: id,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      const items = agentResponse.data.data?.items ?? [];
      const meta = agentResponse.data.data;

      if (items.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        setAgents(items);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      }
    } catch (err) {
      toast({
        title: "삭제 실패",
        description: "에이전트 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      console.error(err);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG_ADMIN[status];
    const statusComplete = status === "completed";
    return (
      <div
        className={`inline-flex items-center gap-1 w-[62px] justify-center rounded px-[6.5px] leading-6 text-base tracking-[-0.01em] ${config.bg} ${config.text} ${statusComplete ? "px-[21px]" : " px-[12.5px]"}`}
      >
        <p>{config.label}</p>
      </div>
    );
  };

  const isAbleEdit = (status: IAgentStatus) => {
    return status === "completed" || status === "under_review";
  };

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="min-h-screen p-8" style={{ display: loading ? "none" : "block" }}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button onClick={handleBack} variant="ghost" size="icon" className="w-12 h-12 ">
            <img src={icArrowLeft} alt="Back" className="w-6 h-6" />
          </Button>
          <h1 className="text-[20px] font-bold text-[#000000]">{userName}님의 에이전트</h1>
        </div>

        {/* Body Card */}
        <div className="bg-white rounded-[16px] p-5">
          {/* Table */}
          <div className="border border-[rgba(0,0,0,0.09)] rounded-[4px] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#EBEBEB] border-b border-[rgba(0,0,0,0.09)]">
                  <TableHead>에이전트 명</TableHead>
                  <TableHead>생성일</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center p-8 text-[#000000]">
                      에이전트가 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  agents.map((agent) => (
                    <TableRow
                      key={agent.id}
                      className="border-b border-[rgba(0,0,0,0.09)] last:border-b-0"
                    >
                      <TableCell className="p-3 w-[386px]">
                        <div className="flex flex-col">
                          <span className="text-[16px] text-[#000000] font-medium">
                            {agent.name}
                          </span>
                          <span className="text-[14px] text-[#000000] mt-1">
                            {agent.short_description}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="p-3 w-[319px]">
                        <span className="text-[16px] text-[#000000]">
                          {formatDate(agent.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="p-3 w-[145px]">
                        {getStatusBadge(agent.status)}
                      </TableCell>
                      <TableCell className="p-3 w-[197px]">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px]  text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                            onClick={() => handleDeleteClick(agent)}
                          >
                            삭제
                          </Button>
                          {isAbleEdit(agent.status) ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-[80px] h-[32px]  text-[12px] font-bold text-[#000000] border border-[rgba(0,0,0,0.15)]"
                              onClick={() => navigate(`/agents/${agent.id}/edit`)}
                            >
                              수정
                            </Button>
                          ) : (
                            <div className="w-[80px]" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          variant="danger"
          title="에이전트 삭제 확인"
          description={`해당 에이전트를 삭제하시겠습니까?`}
          confirmText="네"
          cancelText="아니요"
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

export default ManageUserAgentListPage;
